import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import {
	useCreatedDistrictManagers,
	useResellerMqttSources,
} from "@/api/reseller/queries";
import type { MqttSource } from "@/api/reseller/types";
import {
	type Column,
	MeterTable,
	usePagination,
} from "@/components/meters/meter-table";
import { CopyableCell } from "@/components/shared/copyable-cell";
import { Badge } from "@/components/ui/badge";
import { EmptyCell } from "@/components/ui/empty-cell";
import { isValidLang, type Lang, ROUTE_SLUGS } from "@/config/routes";
import { useDebounce } from "@/hooks/use-debounce";
import { AssignToDistrictManagerDialog } from "./assign-to-district-manager-dialog";
import { GatewaySearchCombobox } from "./gateway-search-combobox";
import { ResellerMqttRowActions } from "./reseller-mqtt-row-actions";

export function ResellerMqttTable() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const { lang } = useParams<{ lang: string }>();
	const currentLang: Lang = lang && isValidLang(lang) ? lang : "pl";
	const { page, setPage } = usePagination();
	const [pageSize, setPageSize] = useState(20);
	const [searchSn, setSearchSn] = useState("");
	const [comboQuery, setComboQuery] = useState("");
	const debouncedComboQuery = useDebounce(comboQuery);
	const [assignSn, setAssignSn] = useState<string | null>(null);
	const [assignManagerId, setAssignManagerId] = useState<number | null>(null);

	const [ordering, setOrdering] = useState("");
	const { data, isLoading } = useResellerMqttSources({
		page,
		page_size: pageSize,
		search: searchSn || undefined,
		ordering: ordering || undefined,
	});

	const { data: managersData } = useCreatedDistrictManagers({ page_size: 100 });
	const managerNames = useMemo(() => {
		const map = new Map<number, string>();
		for (const m of managersData?.results ?? []) map.set(m.id, m.company_name);
		return map;
	}, [managersData]);

	const comboSearch = useResellerMqttSources({
		page_size: 20,
		search: debouncedComboQuery || undefined,
	});
	const comboOptions = (comboSearch.data?.results ?? []).map((s) => ({
		sn: s.sn,
		label: s.district_name ?? undefined,
	}));
	const handleComboSearch = useCallback((q: string) => setComboQuery(q), []);

	function openAssign(row: MqttSource) {
		setAssignSn(row.sn);
		setAssignManagerId(row.district_manager);
	}

	function closeAssign() {
		setAssignSn(null);
		setAssignManagerId(null);
	}

	const columns: Column<MqttSource>[] = [
		{
			key: "sn",
			label: t("mqttSources.colSerialNumber"),
			sortField: "sn",
			render: (row) => (
				<CopyableCell value={row.sn} label={t("mqttSources.colSerialNumber")} />
			),
		},
		{
			key: "district",
			label: t("mqttSources.colDistrict"),
			render: (row) =>
				row.district_name ??
				(row.district != null ? String(row.district) : null) ?? <EmptyCell />,
		},
		{
			key: "district_manager",
			label: t("mqttSources.colDistrictManager"),
			render: (row) =>
				row.district_manager != null ? (
					(managerNames.get(row.district_manager) ??
					String(row.district_manager))
				) : (
					<EmptyCell />
				),
		},
		{
			key: "meters_count",
			label: t("mqttSources.colMeters"),
			render: (row) => row.meters_count,
		},
		{
			key: "next_reading_date",
			label: t("mqttSources.colNextReading"),
			render: (row) =>
				row.next_reading_date ? (
					new Date(row.next_reading_date).toLocaleDateString(i18n.language)
				) : (
					<EmptyCell />
				),
		},
		{
			key: "_details",
			label: t("mqttSources.colLastReading"),
			render: (row) =>
				row.last_reading ? (
					<div className="flex items-center gap-2">
						<Badge variant={row.last_reading.completed ? "success" : "info"}>
							{row.last_reading.completed
								? t("mqttSourceDetail.completed")
								: t("mqttSourceDetail.inProgress")}
						</Badge>
						<span className="text-xs text-muted-foreground">
							{new Date(row.last_reading.reading_date).toLocaleDateString(
								i18n.language,
							)}
						</span>
					</div>
				) : (
					<EmptyCell />
				),
		},
		{
			key: "_actions",
			label: "",
			render: (row) => (
				<ResellerMqttRowActions
					onAssign={() => openAssign(row)}
					onDetails={() =>
						navigate(
							`/${currentLang}/${ROUTE_SLUGS.sources[currentLang]}/${row.sn}`,
						)
					}
				/>
			),
		},
	];

	return (
		<>
			<MeterTable
				data={data}
				columns={columns}
				loading={isLoading}
				page={page}
				pageSize={pageSize}
				onPageChange={setPage}
				onPageSizeChange={(size) => {
					setPageSize(size);
					setPage(1);
				}}
				ordering={ordering}
				onOrderingChange={(val) => {
					setOrdering(val);
					setPage(1);
				}}
				headerActions={
					<GatewaySearchCombobox
						value={searchSn}
						onChange={(sn) => {
							setSearchSn(sn);
							setPage(1);
						}}
						options={comboOptions}
						isLoading={comboSearch.isLoading}
						onSearch={handleComboSearch}
					/>
				}
			/>
			{assignSn ? (
				<AssignToDistrictManagerDialog
					open={!!assignSn}
					onOpenChange={(o) => !o && closeAssign()}
					sn={assignSn}
					currentManagerId={assignManagerId}
				/>
			) : null}
		</>
	);
}
